/**
 * Utility functions for Parts Catalog
 * Includes CSV export, filtering, and data formatting functions
 */

/**
 * Filter parts based on search term (client-side filtering)
 */
export const filterParts = (parts, searchTerm) => {
  if (!searchTerm) return parts;
  
  const searchLower = searchTerm.toLowerCase();
  return parts.filter((part) => {
    return (
      part.partNumber?.toLowerCase().includes(searchLower) ||
      part.referencePartNumber?.toLowerCase().includes(searchLower) ||
      part.partName?.toLowerCase().includes(searchLower) ||
      part.description?.toLowerCase().includes(searchLower) ||
      part.category?.name?.toLowerCase().includes(searchLower) ||
      part.manufacturer?.name?.toLowerCase().includes(searchLower) ||
      (part.manufacturers && Array.isArray(part.manufacturers) && 
        part.manufacturers.some(m => m?.name?.toLowerCase().includes(searchLower))) ||
      part.partType?.toLowerCase().includes(searchLower) ||
      part.unitMetric?.toLowerCase().includes(searchLower) ||
      (part.primaryModels && Array.isArray(part.primaryModels) && 
        part.primaryModels.some(m => m?.name?.toLowerCase().includes(searchLower))) ||
      (part.model && part.model.name?.toLowerCase().includes(searchLower))
    );
  });
};

/**
 * Helper function to escape CSV values
 */
const escapeCSV = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  // If value contains comma, newline, or quote, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

/**
 * Helper function to format arrays for CSV
 */
const formatArray = (arr) => {
  if (!arr || !Array.isArray(arr) || arr.length === 0) return '';
  return arr.map(item => {
    if (typeof item === 'object' && item !== null) {
      return item.name || item.code || JSON.stringify(item);
    }
    return String(item);
  }).join('; ');
};

/**
 * Helper function to format date for CSV
 */
const formatDate = (date) => {
  if (!date) return '';
  try {
    return new Date(date).toLocaleString();
  } catch (e) {
    return String(date);
  }
};

/**
 * Convert parts data to CSV format
 */
export const convertToCSV = (partsData) => {
  if (!partsData || partsData.length === 0) {
    return '';
  }

  // Define all CSV columns based on Part model schema
  const headers = [
    'Part ID',
    'Part Number',
    'Part Name',
    'Description',
    'Unit Metric',
    'Frequency',
    'Category',
    'Serial Number Required',
    'Serial Number',
    'ABC Type',
    'Folio Number',
    'Part Type',
    'ATA Chapter Code',
    'ATA Chapter Name',
    'IPC Reference',
    'Bin Card Number',
    'Item Tags',
    'Approximate Rate',
    'Manufacturer (Legacy)',
    'Manufacturers',
    'Model (Legacy)',
    'Primary Models',
    'Model Variants',
    'Kit',
    'Expiry Period (Months)',
    'Expiry Type',
    'Expiry Value / Specific Date',
    'In Quarters',
    'Expiry Item',
    'Storage Life (Months)',
    'AMM/CMM Reference',
    'Minimum Level',
    'Maximum Level',
    'Reorder Level Required',
    'Reorder Level',
    'Show in Valuation Report',
    'One Time Purchase',
    'Airworthiness Check Required',
    'Show in Stock Report',
    'Part Not In Use',
    'Not In Use Date',
    'Note',
    'Applicable Assembly Types',
    'Status',
    'Created At',
    'Updated At'
  ];

  // Build CSV rows
  const rows = partsData.map(part => {
    return [
      escapeCSV(part.partId || ''),
      escapeCSV(part.partNumber || ''),
      escapeCSV(part.partName || ''),
      escapeCSV(part.description || ''),
      escapeCSV(part.unitMetric || ''),
      escapeCSV(part.frequency || ''),
      escapeCSV(part.category?.name || ''),
      escapeCSV(part.serialNumberRequired ? 'Yes' : 'No'),
      escapeCSV(part.serialNumber || ''),
      escapeCSV(part.abcType || ''),
      escapeCSV(part.folioNumber || ''),
      escapeCSV(part.partType || ''),
      escapeCSV(part.ataChapter?.code || ''),
      escapeCSV(part.ataChapter?.name || ''),
      escapeCSV(part.ipcReference || ''),
      escapeCSV(part.binCardNumber || ''),
      escapeCSV(Array.isArray(part.itemTags) ? part.itemTags.join('; ') : ''),
      escapeCSV(part.approximateRate || ''),
      escapeCSV(part.manufacturer?.name || ''),
      escapeCSV(formatArray(part.manufacturers)),
      escapeCSV(part.model?.name || ''),
      escapeCSV(formatArray(part.primaryModels)),
      escapeCSV(formatArray(part.modelVariants)),
      escapeCSV(part.kit ? 'Yes' : 'No'),
      escapeCSV(part.expiryPeriodInMonths || ''),
      escapeCSV(part.expiryType || ''),
      escapeCSV(
        part.expiryType === 'specific_date' && part.expirySpecificDate
          ? formatDate(part.expirySpecificDate)
          : (part.expiryPeriodValue != null ? String(part.expiryPeriodValue) : '')
      ),
      escapeCSV(part.inQuarters || ''),
      escapeCSV(part.expiryItem ? 'Yes' : 'No'),
      escapeCSV(part.storageLifeInMonth || ''),
      escapeCSV(part.ammCmmReference || ''),
      escapeCSV(part.minimumLevel || ''),
      escapeCSV(part.maximumLevel || ''),
      escapeCSV(part.reOrderLevelRequired ? 'Yes' : 'No'),
      escapeCSV(part.reOrderLevel || ''),
      escapeCSV(part.showInValuationReport ? 'Yes' : 'No'),
      escapeCSV(part.oneTimePurchase ? 'Yes' : 'No'),
      escapeCSV(part.airworthinessCheckRequired ? 'Yes' : 'No'),
      escapeCSV(part.showInStockReport ? 'Yes' : 'No'),
      escapeCSV(part.partNotInUse ? 'Yes' : 'No'),
      escapeCSV(formatDate(part.notInUseDate)),
      escapeCSV(part.note || ''),
      escapeCSV(formatArray(part.applicableAssemblyTypes)),
      escapeCSV(part.isActive ? 'Active' : 'Archived'),
      escapeCSV(formatDate(part.createdAt)),
      escapeCSV(formatDate(part.updatedAt))
    ];
  });

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Export parts to CSV file
 */
export const exportPartsToCSV = (filteredParts) => {
  return () => {
    try {
      // Export the currently filtered parts (client-side filtered)
      if (filteredParts.length === 0) {
        alert('No parts found to export');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(filteredParts);
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `parts-catalog-export-${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      alert(`Successfully exported ${filteredParts.length} parts to CSV`);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Error exporting parts to CSV. Please try again.');
    }
  };
};

/**
 * Prepare form data for editing a part
 */
export const prepareEditFormData = (part) => {
  // Load arrays from backend (support both new array fields and legacy single fields)
  const manufacturerIds = part.manufacturers && Array.isArray(part.manufacturers) && part.manufacturers.length > 0
    ? part.manufacturers.map(m => m._id || m)
    : (part.manufacturer?._id || part.manufacturer ? [part.manufacturer._id || part.manufacturer] : []);
  
  const primaryModelIds = part.primaryModels && Array.isArray(part.primaryModels) && part.primaryModels.length > 0
    ? part.primaryModels.map(m => m._id || m)
    : (part.model?._id || part.model ? [part.model._id || part.model] : []);
  
  const modelVariantIds = part.modelVariants && Array.isArray(part.modelVariants) && part.modelVariants.length > 0
    ? part.modelVariants.map(m => m._id || m)
    : [];
  
  // Handle serial numbers: when not required, do not load stale values so they don't reappear when user checks the box
  let serialNumbers = [''];
  if (part.serialNumberRequired) {
    if (part.serialNumbers && Array.isArray(part.serialNumbers) && part.serialNumbers.length > 0) {
      serialNumbers = part.serialNumbers.filter(sn => sn && sn.trim());
      if (serialNumbers.length === 0) serialNumbers = [''];
    } else if (part.serialNumber && part.serialNumber.trim()) {
      serialNumbers = [part.serialNumber];
    }
  }
  
  // Expiry: support new fields and legacy expiryPeriodInMonths
  const hasSpecificDate = part.expiryType === 'specific_date' && part.expirySpecificDate;
  const expiryType = part.expiryType || (part.expiryPeriodInMonths != null ? 'months' : 'months');
  const expiryPeriodValue = part.expiryPeriodValue ?? part.expiryPeriodInMonths ?? '';
  const expirySpecificDate = hasSpecificDate
    ? (typeof part.expirySpecificDate === 'string' ? part.expirySpecificDate : part.expirySpecificDate?.toISOString?.())
    : '';

  return {
    partNumber: part.partNumber,
    referencePartNumber: part.referencePartNumber || '',
    partName: part.partName || '',
    description: part.description,
    unitMetric: part.unitMetric,
    frequency: part.frequency || '',
    category: part.category?._id || part.category || '',
    manufacturerNames: manufacturerIds,
    primaryModels: primaryModelIds,
    modelVariants: modelVariantIds,
    partType: part.partType,
    ataChapter: part.ataChapter?._id || part.ataChapter || '',
    serialNumberRequired: part.serialNumberRequired || false,
    serialNumbers: serialNumbers,
    applicableAssemblyTypes: part.applicableAssemblyTypes?.map(at => at._id || at) || [],
    expiryItem: part.expiryItem || false,
    expiryType,
    expirySpecificDate,
    expiryPeriodValue,
    expiryPeriodInMonths: part.expiryPeriodInMonths ?? '',
    storageLifeInMonth: part.storageLifeInMonth ?? ''
  };
};

/**
 * Get initial form data for new part
 */
export const getInitialFormData = () => {
  return {
    partNumber: '',
    referencePartNumber: '',
    partName: '',
    description: '',
    unitMetric: '',
    frequency: '',
    category: '',
    manufacturerNames: [],
    primaryModels: [],
    modelVariants: [],
    partType: 'Standard Part',
    ataChapter: '',
    serialNumberRequired: false,
    serialNumbers: [''],
    applicableAssemblyTypes: [],
    expiryItem: false,
    expiryType: 'months',
    expirySpecificDate: '',
    expiryPeriodValue: '',
    expiryPeriodInMonths: '',
    storageLifeInMonth: ''
  };
};
